import useSettings from "@/Hooks/useSettings";
import { cn } from "@/utils/functions";
import {
  FileDoneOutlined,
  FileOutlined,
  FileSyncOutlined,
  HomeOutlined,
  SettingOutlined,
  SolutionOutlined,
  UnorderedListOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import { useEffect, useRef, useState } from "react";
import { AiOutlineFileProtect } from "react-icons/ai";
import { FaBusinessTime } from "react-icons/fa";
import { GoTasklist } from "react-icons/go";
import { GrUserAdmin } from "react-icons/gr";
import { HiOutlineUserGroup } from "react-icons/hi";
import { HiOutlineBuildingOffice2 } from "react-icons/hi2";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import {
  MdEmail,
  MdOutlinePermMedia,
  MdOutlineVerifiedUser,
  MdPolicy,
} from "react-icons/md";
import { TbAward, TbShoppingCartCog } from "react-icons/tb";
import { useSelector } from "react-redux";
import { NavLink } from "react-router-dom";
import Menu from "../../UI/Menu";
import usePermissions from "../../utils/usePermissions";
import useSettingNav from "./SettingNav";
import SideNavLoader from "./SideNavLoader";

const SideNav = ({ collapsed, setCollapsed }) => {
  const { permissions } = usePermissions();
  const [active, setActive] = useState({
    isSetting: false,
  });
  const { loading } = useSelector((state) => state.auth);
  const { isSaleCommission, isPos } = useSettings([
    "isSaleCommission",
    "isPos",
  ]);

  const menuContainerRef = useRef(null);

  const menu = [
    Array.isArray(permissions) &&
    permissions.length > 0 && {
      permit: {
        permissions: ["readAll-dashboard", "create-dashboard"],
        operator: "or",
      },
      label: (
        <NavLink to="/admin/dashboard">
          <span>DASHBOARD</span>
        </NavLink>
      ),
      key: "dashboard",
      icon: <HomeOutlined />,
    },

    {
      label: "HR",
      permit: {
        permissions: [
          "create-user",
          "readAll-user",
          "create-role",
          "readAll-role",
          "create-rolePermission",
          "readAll-rolePermission",
          "create-designation",
          "readAll-designation",
          "create-department",
          "readAll-department",
          "create-shift",
          "readAll-shift",
          "create-employmentStatus",
          "readAll-employmentStatus",
        ],
        operator: "or",
      },
      key: "hr",
      icon: <TbShoppingCartCog />,
      children: [
        {
          label: (
            <NavLink to="/admin/add-staff">
              <span>New Employee</span>
            </NavLink>
          ),
          permit: {
            permissions: [
              "create-user",
              "readAll-user",
              "readSingle-user",
              "update-user",
              "delete-user",
            ],
            operator: "or",
          },
          key: "staff",
          icon: <HiOutlineUserGroup />,
        },
        {
          label: (
            <NavLink to="/admin/staff">
              <span>Employee List</span>
            </NavLink>
          ),
          permit: {
            permissions: [
              "create-user",
              "readAll-user",
              "readSingle-user",
              "update-user",
              "delete-user",
            ],
            operator: "or",
          },
          key: "staff",
          icon: <HiOutlineUserGroup />,
        },
        {
          label: (
            <NavLink to="/admin/role">
              <span>Role & Permissions</span>
            </NavLink>
          ),
          permit: {
            permissions: [
              "create-role",
              "readAll-role",
              "readSingle-role",
              "update-role",
              "delete-role",
            ],
            operator: "or",
          },
          key: "role",
          icon: <GrUserAdmin />,
        },
        {
          label: (
            <NavLink to="/admin/department">
              <span>Department</span>
            </NavLink>
          ),
          key: "department",
          icon: <HiOutlineBuildingOffice2 />,
        },
        {
          permit: {
            permissions: [
              "create-designation",
              "readAll-designation",
              "readSingle-designation",
              "update-designation",
              "delete-designation",
            ],
            operator: "or",
          },
          label: (
            <NavLink to="/admin/designation">
              <span>Designation</span>
            </NavLink>
          ),
          key: "designation",
          icon: <SolutionOutlined />,
        },
      ],
    },
    {
      permit: {
        permissions: ["create-attendance", "readAll-attendance"],
        operator: "or",
      },
      label: (
        <NavLink to="/admin/attendance">
          <span>Attendance</span>
        </NavLink>
      ),
      key: "attendance",
      icon: <MdOutlineVerifiedUser />,
    },
    {
      permit: {
        permissions: ["create-payroll", "readAll-payroll"],
        operator: "or",
      },
      label: "Payroll",
      key: "payroll",
      icon: <TbShoppingCartCog />,
      children: [
        {
          permit: {
            permissions: [
              "create-payroll",
              "readAll-payroll",
              "readSingle-payroll",
              "update-payroll",
              "delete-payroll",
            ],
            operator: "or",
          },
          label: (
            <NavLink to="/admin/calculate-payroll">
              <span>Calculate Payroll</span>
            </NavLink>
          ),
          key: "calculate-payroll",
          icon: <MdPolicy />,
        },
        {
          permit: {
            permissions: [
              "create-payroll",
              "readAll-payroll",
              "readSingle-payroll",
              "update-payroll",
              "delete-payroll",
            ],
            operator: "or",
          },
          label: (
            <NavLink to="/admin/payslip">
              <span>Payslip List</span>
            </NavLink>
          ),
          key: "payslip",
          icon: <MdPolicy />,
        },
      ],
    },
    {
      permit: {
        permissions: ["create-shift", "readAll-shift"],
        operator: "or",
      },
      label: (
        <NavLink to="/admin/shift">
          <span>Shift</span>
        </NavLink>
      ),

      key: "shift",
      icon: <FaBusinessTime />,
    },
    {
      permit: {
        permissions: ["create-employmentStatus", "readAll-employmentStatus"],
        operator: "or",
      },
      label: (
        <NavLink to="/admin/employment-status">
          <span>Employment Status</span>
        </NavLink>
      ),
      key: "employmentStatus",
      icon: <FaBusinessTime />,
    },
    {
      permit: {
        permissions: [
          "create-leaveApplication",
          "readAll-leaveApplication",
          "readSingle-leaveApplication",
          "update-leaveApplication",
          "delete-leaveApplication",
        ],
        operator: "or",
      },
      label: (
        <NavLink to="/admin/leave">
          <span>Leave</span>
        </NavLink>
      ),
      key: "Leave",
      icon: <AiOutlineFileProtect />,
    },
    {
      permit: {
        permissions: [
          "create-publicHoliday",
          "readAll-publicHoliday",
          "crate-weeklyHoliday",
          "readAll-weeklyHoliday",
        ],
        operator: "or",
      },
      label: "Holiday",
      key: "holiday",
      icon: <TbShoppingCartCog />,
      children: [
        {
          permit: {
            permissions: [
              "create-weeklyHoliday",
              "readAll-weeklyHoliday",
              "readSingle-weeklyHoliday",
              "update-weeklyHoliday",
              "delete-weeklyHoliday",
            ],
            operator: "or",
          },
          label: (
            <NavLink to="/admin/weekly-holiday">
              <span>Weekly Holiday</span>
            </NavLink>
          ),
          key: "weekly-holiday",
          icon: <MdPolicy />,
        },
        {
          permit: {
            permissions: [
              "create-publicHoliday",
              "readAll-publicHoliday",
              "readSingle-publicHoliday",
              "update-publicHoliday",
              "delete-publicHoliday",
            ],
            operator: "or",
          },
          label: (
            <NavLink to="/admin/public-holiday">
              <span>Public Holiday</span>
            </NavLink>
          ),
          key: "public-holiday",
          icon: <MdPolicy />,
        },
      ],
    },
    {
      permit: {
        permissions: ["create-leavePolicy", "readAll-leavePolicy"],
        operator: "or",
      },
      label: (
        <NavLink to="/admin/leave-policy">
          <span>Leave Policy</span>
        </NavLink>
      ),
      key: "leave-policy",
      icon: <MdPolicy />,
    },
    {
      permit: {
        permissions: ["create-announcement", "readAll-announcement"],
        operator: "or",
      },

      label: (
        <NavLink to="/admin/announcement">
          <span>Announcement</span>
        </NavLink>
      ),
      key: "announcement",
      icon: <MdPolicy />,
    },
    {
      label: "ACCOUNTS",
      permit: {
        permissions: [
          "create-account",
          "readAll-account",
          "create-transaction",
          "readAll-transaction",
          "create-productReports",
          "readAll-productReports",
        ],
        operator: "or",
      },
      key: "accounts",
      icon: <WalletOutlined />,
      children: [
        {
          permit: {
            permissions: [
              "create-account",
              "readAll-account",
              "readSingle-account",
              "update-account",
              "delete-account",
            ],
            operator: "or",
          },
          label: (
            <NavLink to="/admin/account/">
              <span>Account</span>
            </NavLink>
          ),
          key: "accountList",
          icon: <UnorderedListOutlined />,
        },
        {
          permit: {
            permissions: [
              "create-transaction",
              "readAll-transaction",
              "readSingle-transaction",
              "update-transaction",
              "delete-transaction",
            ],
            operator: "or",
          },
          label: (
            <NavLink to="/admin/transaction/">
              <span>Transaction</span>
            </NavLink>
          ),
          key: "transactionList",
          icon: <UnorderedListOutlined />,
        },
      ],
    },
    {
      label: "Reports",
      permit: {
        permissions: ["create-account", "readAll-account"],
        operator: "or",
      },
      key: "reports",
      icon: <WalletOutlined />,
      children: [
        {
          permit: {
            permissions: [
              "create-account",
              "readAll-account",
              "readSingle-account",
              "update-account",
              "delete-account",
            ],
            operator: "or",
          },
          label: (
            <NavLink to="/admin/report/trial-balance">
              <span>Trial Balance</span>
            </NavLink>
          ),
          key: "trialBalance",
          icon: <FileDoneOutlined />,
        },
        {
          permit: {
            permissions: [
              "create-account",
              "readAll-account",
              "readSingle-account",
              "update-account",
              "delete-account",
            ],
            operator: "or",
          },
          label: (
            <NavLink to="/admin/report/balance-sheet">
              <span>Balance Sheet</span>
            </NavLink>
          ),
          key: "balanceSheet",
          icon: <FileOutlined />,
        },
        {
          permit: {
            permissions: [
              "create-account",
              "readAll-account",
              "readSingle-account",
              "update-account",
              "delete-account",
            ],
            operator: "or",
          },
          label: (
            <NavLink to="/admin/report/income">
              <span>Income Statement</span>
            </NavLink>
          ),
          key: "incomeStatement",
          icon: <FileSyncOutlined />,
        },
      ],
    },
    {
      permit: {
        permissions: ["create-award", "readAll-award"],
        operator: "or",
      },
      label: (
        <NavLink to="/admin/award">
          <span>Award</span>
        </NavLink>
      ),
      key: "award",
      icon: <TbAward />,
    },
    {
      permit: {
        permissions: [
          "create-project",
          "readAll-project",
          "create-projectTeam",
          "readAll-projectTeam",
          "create-priority",
          "readAll-priority",
        ],
        operator: "or",
      },
      label: "Project",
      key: "project",
      icon: <SettingOutlined />,
      children: [
        {
          permit: {
            permissions: [
              "create-project",
              "readAll-project",
              "readSingle-project",
              "update-project",
              "delete-project",
            ],
            operator: "or",
          },
          label: (
            <NavLink to="/admin/project">
              <span>Project</span>
            </NavLink>
          ),
          key: "project",
          icon: <SettingOutlined />,
        },
        {
          permit: {
            permissions: [
              "create-projectTeam",
              "readAll-projectTeam",
              "readSingle-projectTeam",
              "update-projectTeam",
              "delete-projectTeam",
            ],
            operator: "or",
          },
          label: (
            <NavLink to="/admin/team">
              <span>Team</span>
            </NavLink>
          ),
          key: "team",
          icon: <SettingOutlined />,
        },
        {
          permit: {
            permissions: [
              "create-priority",
              "readAll-priority",
              "readSingle-priority",
              "update-priority",
              "delete-priority",
            ],
            operator: "or",
          },
          label: (
            <NavLink to="/admin/task-priority">
              <span>Task Priority</span>
            </NavLink>
          ),
          key: "task-priority",
          icon: <SettingOutlined />,
        },
      ],
    },
    {
      permit: {
        permissions: ["create-tasks", "readAll-tasks"],
        operator: "or",
      },
      label: (
        <NavLink to="/admin/task">
          <span>Task</span>
        </NavLink>
      ),
      key: "task global",
      icon: <GoTasklist />,
    },
    {
      permit: {
        permissions: [
          "create-job",
          "readAll-job",
          "create-jobCategory",
          "readAll-jobCategory",
          "create-jobType",
          "readAll-jobType",
          "create-jobLocation",
          "readAll-jobLocation",
          "create-jobSkills",
          "readAll-jobSkills",
          "create-jobWorkExperience",
          "readAll-jobWorkExperience",
          "create-job",
          "readAll-job",
          "create-jobApplication",
          "readAll-jobApplication",
          "create-jobApplicationStatus",
          "readAll-jobApplicationStatus",
          "create-jobInterview",
          "readAll-jobInterview",
        ],
        operator: "or",
      },

      label: "Recruitment",
      key: "Recruitment",
      icon: <TbShoppingCartCog />,
      children: [
        {
          permit: {
            operator: "or",
            permissions: [
              "create-jobCategory",
              "readAll-jobCategory",
              "readSingle-jobCategory",
              "update-jobCategory",
              "delete-jobCategory",
            ],
          },
          label: (
            <NavLink to="/admin/job-category">
              <span>Job Category</span>
            </NavLink>
          ),
          key: "job-category",
          icon: <MdPolicy />,
        },
        {
          permit: {
            permissions: [
              "create-jobType",
              "readAll-jobType",
              "readSingle-jobType",
              "update-jobType",
              "delete-jobType",
            ],
            operator: "or",
          },
          label: (
            <NavLink to="/admin/job-type">
              <span>Job Type</span>
            </NavLink>
          ),
          key: "job-type",
          icon: <MdPolicy />,
        },
        {
          permit: {
            permissions: [
              "create-jobLocation",
              "readAll-jobLocation",
              "readSingle-jobLocation",
              "update-jobLocation",
              "delete-jobLocation",
            ],
            operator: "or",
          },
          label: (
            <NavLink to="/admin/job-location">
              <span>Job Location</span>
            </NavLink>
          ),
          key: "job-location",
          icon: <MdPolicy />,
        },
        {
          permit: {
            permissions: [
              "create-jobSkills",
              "readAll-jobSkills",
              "readSingle-jobSkills",
              "update-jobSkills",
              "delete-jobSkills",
            ],
            operator: "or",
          },
          label: (
            <NavLink to="/admin/job-skills">
              <span>Job Skills</span>
            </NavLink>
          ),
          key: "job-skills",
          icon: <MdPolicy />,
        },
        {
          permit: {
            permissions: [
              "create-jobWorkExperience",
              "readAll-jobWorkExperience",
              "readSingle-jobWorkExperience",
              "update-jobWorkExperience",
              "delete-jobWorkExperience",
            ],
            operator: "or",
          },
          label: (
            <NavLink to="/admin/job-experience">
              <span>Job Work Experience</span>
            </NavLink>
          ),
          key: "job-experience",
          icon: <MdPolicy />,
        },
        {
          permit: {
            permissions: [
              "create-job",
              "readAll-job",
              "readSingle-job",
              "update-job",
              "delete-job",
            ],
            operator: "or",
          },
          label: (
            <NavLink to="/admin/job">
              <span>Job</span>
            </NavLink>
          ),
          key: "job",
          icon: <MdPolicy />,
        },
        {
          permit: {
            permissions: [
              "create-jobApplication",
              "readAll-jobApplication",
              "readSingle-jobApplication",
              "update-jobApplication",
              "delete-jobApplication",
            ],
            operator: "or",
          },
          label: (
            <NavLink to="/admin/job-application">
              <span>Job Application</span>
            </NavLink>
          ),
          key: "job-application",
          icon: <MdPolicy />,
        },
        {
          permit: {
            permissions: [
              "create-jobApplicationStatus",
              "readAll-jobApplicationStatus",
              "readSingle-jobApplicationStatus",
              "update-jobApplicationStatus",
              "delete-jobApplicationStatus",
            ],
            operator: "or",
          },
          label: (
            <NavLink to="/admin/job-board">
              <span>Job Board</span>
            </NavLink>
          ),
          key: "job-board",
          icon: <MdPolicy />,
        },
        {
          permit: {
            permissions: [
              "create-jobInterview",
              "readAll-jobInterview",
              "readSingle-jobInterview",
              "update-jobInterview",
              "delete-jobInterview",
            ],
            operator: "or",
          },
          label: (
            <NavLink to="/admin/job-interview">
              <span>Job Interview</span>
            </NavLink>
          ),
          key: "job-interview",
          icon: <MdPolicy />,
        },
        {
          permit: {
            permissions: [
              "create-job",
              "readAll-job",
              "readSingle-job",
              "update-job",
              "delete-job",
            ],
            operator: "or",
          },
          label: (
            <NavLink to="/recruitment">
              <span>Job Desk</span>
            </NavLink>
          ),
          key: "job-desk",
          icon: <MdPolicy />,
        },
      ],
    },

    {
      label: (
        <NavLink to="/admin/media">
          <span>MEDIA</span>
        </NavLink>
      ),
      permit: {
        permissions: ["create-media", "readAll-media"],
        operator: "or",
      },
      key: "media",
      icon: <MdOutlinePermMedia />,
    },
    {
      label: (
        <NavLink to="/admin/email">
          <span>EMAIL</span>
        </NavLink>
      ),
      permit: {
        permissions: ["create-email", "readAll-email"],
        operator: "or",
      },
      key: "EMAIL",
      icon: <MdEmail />,
    },
  ];

  const SettingMenu = useSettingNav();

  useEffect(() => {
    if (active.isSetting && menuContainerRef.current) {
      menuContainerRef.current.scrollTo({
        top: 0,
        // behavior: "smooth",
      });
    }
  }, [active.isSetting]);
  return (
    <div
      ref={menuContainerRef}
      className="
    overflow-y-auto no-scrollbar
    h-[calc(100vh-100px)]
    pb-4 bg-transparent
    text-white
  "
    >
      {loading ? (
        <SideNavLoader />
      ) : (
        <div className="relative">
          <div
            className={cn(
              `absolute w-full  transition-all duration-300 ${active.isSetting ? "left-[280px]" : "left-0"
              }`
            )}
          >
            <Menu
              items={menu}
              setCollapsed={setCollapsed}
              permissions={permissions}
              collapsed={collapsed}
            />
            {Array.isArray(permissions) && permissions.length > 0 && (
              <>
                {/* settings */}
                <div
                  className={cn(
                    "px-4 flex items-center justify-between font-Popins py-3 cursor-pointer",
                    {
                      "flex items-center justify-center px-0 text-lg":
                        collapsed,
                    }
                  )}
                  onClick={() =>
                    setActive({ isCrm: false, isSetting: true, isHrm: false })
                  }
                >
                  <span className="flex items-center gap-1">
                    <SettingOutlined /> {!collapsed && "Settings"}
                  </span>
                  {!collapsed && <IoIosArrowForward />}
                </div>
              </>
            )}
          </div>

          {/* setting menu */}
          <div
            className={cn(
              `absolute w-full  transition-all duration-300 ${active.isSetting ? "left-0" : "-left-[280px]"
              }`
            )}
          >
            <div
              className={cn(
                "px-4 flex items-center font-medium gap-1 font-Popins bg-sideNavBg text-white hover:bg-sideNavBg py-3 cursor-pointer",
                {
                  "flex items-center justify-center text-lg": collapsed,
                }
              )}
              onClick={() =>
                setActive({ isCrm: false, isSetting: false, isHrm: false })
              }
            >
              <IoIosArrowBack /> {!collapsed && "Back to menu"}
            </div>
            <hr className=" border-gray-300" />
            <Menu
              items={SettingMenu}
              setCollapsed={setCollapsed}
              permissions={permissions}
              collapsed={collapsed}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SideNav;
